// SUMMARY: App launcher grid - the shared 3-column tile grid of the product's main destinations.

'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Brain, CreditCard, FileText, FolderKanban, Globe, MessageSquare, User, type LucideIcon } from 'lucide-react'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { PROJECTS_ENABLED } from '@/lib/features'
import { cn } from '@/lib/utils'

export type AppItem = {
	key: string
	href: string
	icon: LucideIcon
}

export type AppGridVariant = 'plain' | 'menu'

export const APP_ITEMS: AppItem[] = [
	{ key: 'chat', href: '/dashboard/chat', icon: MessageSquare },
	{ key: 'research', href: '/dashboard/chat', icon: Globe },
	...(PROJECTS_ENABLED
		? [
				{ key: 'documents', href: '/dashboard/projects', icon: FileText },
				{ key: 'projects', href: '/dashboard/projects', icon: FolderKanban },
			]
		: []),
	{ key: 'memory', href: '/dashboard/settings/memory', icon: Brain },
	{ key: 'billing', href: '/dashboard/settings/billing', icon: CreditCard },
	{ key: 'account', href: '/dashboard/settings/account', icon: User },
]

const TILE_CLASS =
	'flex flex-col items-center gap-1.5 rounded-lg px-2 py-4 text-center cursor-pointer transition-colors hover:bg-accent'

function AppTile({
	item,
	label,
	variant,
	onNavigate,
}: {
	item: AppItem
	label: string
	variant: AppGridVariant
	onNavigate?: () => void
}) {
	const Icon = item.icon

	// In the menu variant the tile class goes on DropdownMenuItem (it tailwind-merges against the item's own
	// px/py/rounded/text base); passing it to the Link instead would leave both sets in the class string.
	const link = (
		<Link
			href={item.href}
			onClick={onNavigate}
			className={variant === 'menu' ? undefined : cn(TILE_CLASS, 'focus-visible:bg-accent focus-visible:outline-none')}
		>
			{/* size-6, not h-6 w-6: DropdownMenuItem force-sizes any svg whose class lacks "size-" down to size-4. */}
			<Icon className="size-6 text-blue-600 dark:text-blue-400" />
			<span className="text-xs font-medium">{label}</span>
		</Link>
	)

	// Menu tiles must be real menu items: Radix blocks Tab inside menu content, so a plain link would be
	// unreachable by keyboard, and only an item's select closes the dropdown on click.
	if (variant === 'menu') {
		return (
			<DropdownMenuItem asChild className={TILE_CLASS}>
				{link}
			</DropdownMenuItem>
		)
	}

	return link
}

export function AppGrid({ onNavigate, variant = 'plain' }: { onNavigate?: () => void; variant?: AppGridVariant }) {
	const t = useTranslations('nav.apps')

	return (
		// role="group" keeps the menu variant valid: role="menu" only allows menuitem/group/separator children.
		<div role="group" className="grid grid-cols-3 gap-2 p-1">
			{APP_ITEMS.map((item) => (
				<AppTile
					key={item.key}
					item={item}
					label={t(`items.${item.key}.label`)}
					variant={variant}
					onNavigate={onNavigate}
				/>
			))}
		</div>
	)
}
